import React, { useState } from 'react';
import { Calendar } from 'primereact/calendar';
import { useGetAssociateHistoricalActionPlansQuery } from '../../coachingActionSlice';
import { AssociateHistoricalActionPlan } from '../associateCAPTabs-types';
import { metricValueFormat } from '../../../../utils/metricValueFormat';

// ── Helpers ──────────────────────────────────────────────────────────────────
const SESSION_ORDINALS = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth'];

const getSessionLabel = (sessionNumber: number): string => {
    if (sessionNumber === 0) return 'Coaching Started';
    const ordinal = SESSION_ORDINALS[sessionNumber - 1] ?? `${sessionNumber}th`;
    return `${ordinal} Follow Up`;
};

const getDaySuffix = (day: number): string => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
    }
};

const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = date.getDate();
    const suffix = getDaySuffix(day);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric', day: 'numeric' })
        .replace(/\d+,/, `${day}${suffix},`);
};

const toISODate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// ── Component ─────────────────────────────────────────────────────────────────
interface Props {
    empNo: string;
    setShowAcknowledge: React.Dispatch<React.SetStateAction<boolean>>;
    setActionPlanId: React.Dispatch<React.SetStateAction<number>>;
    setSessionId: React.Dispatch<React.SetStateAction<number>>;
    setVia: React.Dispatch<React.SetStateAction<string>>;
}

const getDefaultStartDate = (): Date => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
};

const AssociateCAPHistorical: React.FC<Props> = ({ empNo, setShowAcknowledge, setActionPlanId, setSessionId, setVia }) => {
    const [startDate, setStartDate] = useState<Date | null>(getDefaultStartDate());
    const [endDate, setEndDate] = useState<Date | null>(new Date());

    const bothDatesSelected = startDate !== null && endDate !== null;

    const { data, isLoading, isError } = useGetAssociateHistoricalActionPlansQuery(
        {
            empNo,
            startDate: startDate ? toISODate(startDate) : '',
            endDate: endDate ? toISODate(endDate) : '',
        },
        { skip: !empNo || !bothDatesSelected }
    );

    const plans: AssociateHistoricalActionPlan[] = data?.data ?? [];

    const renderGrid = () => {
        if (!bothDatesSelected) {
            return <p className="cap-tabs__empty">Select a start and end date to view historical records.</p>;
        }
        if (isLoading) {
            return <p className="cap-tabs__empty">Loading…</p>;
        }
        if (isError) {
            return (
                <p className="cap-tabs__empty cap-tabs__empty--error">
                    <i className="pi pi-exclamation-triangle" style={{ marginRight: '0.4rem' }} />
                    Unable to load historical records. Please try again later.
                </p>
            );
        }
        if (plans.length === 0) {
            return <p className="cap-tabs__empty">No historical records found for the selected date range.</p>;
        }
        const onViewClick = (plan: AssociateHistoricalActionPlan) => {
            setActionPlanId(plan.out_coaching_action_plan_id);
            setSessionId(plan.out_coaching_action_plan_session_id);
            setShowAcknowledge(true);
            setVia('historical');
        };
        return (
            <div className="cap-tabs__grid">
                {plans.map((plan) => (
                    <div
                        key={`${plan.out_coaching_action_plan_id}-${plan.out_coaching_action_plan_session_id}`}
                        className="cap-tabs__row"
                    >
                        {/* Metric */}
                        <div className="cap-tabs__cell cap-tabs__cell--metric">
                            <span className="cap-tabs__label">Metric</span>
                            <span className="cap-tabs__metric-name">{plan.out_label}</span>
                        </div>

                        {/* Started / Completed */}
                        <div className="cap-tabs__cell cap-tabs__cell--dates">
                            <span className="cap-tabs__date-line">
                                <strong>Started on</strong>: {formatDate(plan.out_coaching_start_date)}
                            </span>
                            <span className="cap-tabs__date-line cap-tabs__next-step">
                                Completed on: <strong>{formatDate(plan.out_coaching_complete_date)}</strong>
                            </span>
                        </div>

                        <div className="cap-tabs__divider" />

                        {/* Performance Commitment */}
                        <div className="cap-tabs__cell cap-tabs__cell--stat">
                            <span className="cap-tabs__label">Performance Commitment</span>
                            <span className="cap-tabs__stat-value">
                                {plan.out_performance_commitment != null
                                    ? metricValueFormat(plan.out_performance_commitment, plan.out_metric_type_name)
                                    : '—'}
                            </span>
                        </div>

                        <div className="cap-tabs__divider" />

                        {/* Final Metric Score */}
                        <div className="cap-tabs__cell cap-tabs__cell--stat">
                            <span className="cap-tabs__label">Final Metric Score</span>
                            <span className="cap-tabs__stat-value">
                                {plan.out_metric_final_value != null
                                    ? metricValueFormat(plan.out_metric_final_value, plan.out_metric_type_name)
                                    : '—'}
                            </span>
                        </div>

                        <div className="cap-tabs__divider" />

                        {/* Final Session */}
                        <div className="cap-tabs__cell cap-tabs__cell--session">
                            <span className="cap-tabs__label">Final Session</span>
                            <span className="cap-tabs__session-name">
                                {getSessionLabel(plan.out_session_number)}
                            </span>
                        </div>

                        <div className="cap-tabs__divider" />

                        {/* View button */}
                        <div className="cap-tabs__cell cap-tabs__cell--btn">
                            <button className="cap-tabs__btn cap-tabs__btn--view" onClick={() => onViewClick(plan)}>
                                View
                                <i className="pi pi-arrow-circle-right cap-tabs__btn-icon" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <>
            {/* Date range filters */}
            <div className="cap-tabs__filters">
                <div className="cap-tabs__filter-group">
                    <label className="cap-tabs__filter-label" htmlFor="cap-hist-start">
                        Start Date Range
                    </label>
                    <Calendar
                        inputId="cap-hist-start"
                        value={startDate}
                        onChange={(e) => {
                            const selected = e.value as Date | null;
                            setStartDate(selected);
                            // Reset end date if it's now before the new start date
                            if (selected && endDate && endDate < selected) {
                                setEndDate(null);
                            }
                        }}
                        placeholder="Select"
                        dateFormat="dd-M-yy"
                        showIcon
                        className="custom-calendar"
                        inputClassName="cap-tabs__calendar-input"
                    />
                </div>
                <div className="cap-tabs__filter-group">
                    <label className="cap-tabs__filter-label" htmlFor="cap-hist-end">
                        End Date Range
                    </label>
                    <Calendar
                        inputId="cap-hist-end"
                        value={endDate}
                        onChange={(e) => setEndDate(e.value as Date | null)}
                        placeholder="Select"
                        dateFormat="dd-M-yy"
                        minDate={startDate ?? undefined}
                        showIcon
                        className="custom-calendar"
                        inputClassName="cap-tabs__calendar-input"
                    />
                </div>
            </div>

            {renderGrid()}
        </>
    );
};

export default AssociateCAPHistorical;
